// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, useCurrentUser, AppFooter, apiErrorMessage } from "./common";
import { useEscapeKey } from "../components/useEscapeKey";
import { usePermissionsFromSession } from "../components/permissions";

const COLOR_FOR_RISK = { stable: "green", warning: "amber", critical: "rose" };
// 서비스 유형(그룹) 라벨·색상. 캘린더 카드 상단 뱃지·필터에 사용.
// 색상은 도메인 팔레트와 정합: LTC=sky, NLT=orange, ER=rose.
const SERVICE_GROUP_LABEL = { LTC: "장기요양", NLT: "비장기요양", ER: "응급·병원" };
const SERVICE_GROUP_COLOR = { LTC: "sky",    NLT: "orange",       ER: "rose" };
const DAY_LABELS = ['월','화','수','목','금','토','일'];

const STATUS_LABEL = { planned: "예정", in_progress: "진행중", completed: "완료", cancelled: "취소" };
const STATUS_COLOR = { planned: "gray", in_progress: "orange", completed: "green", cancelled: "rose" };
const RISK_LABEL   = { stable: "안정", warning: "주의", critical: "위험" };

// Next status in the planned → in_progress → completed flow. Operators
// hit one button to advance; "cancelled" is a separate destructive action.
const STATUS_NEXT = { planned: "in_progress", in_progress: "completed" };

// Pixel height per hour in the calendar grid. 50px → 25px per 30-min,
// so 45-min and 90-min visits position naturally without rounding.
const PX_PER_HOUR = 50;
const SNAP_MINUTES = 30;

// Default grid window. Expanded automatically when visits fall outside.
const DEFAULT_HOUR_START = 8;
const DEFAULT_HOUR_END   = 18;

// Builds the hour list to render. Always covers [DEFAULT_HOUR_START,
// DEFAULT_HOUR_END]; widens when any visit's start or end hour lies
// outside, so 6am / 22pm visits get their own row instead of being
// clamped to the edge.
function computeHours(visits) {
  let lo = DEFAULT_HOUR_START, hi = DEFAULT_HOUR_END;
  for (const v of visits) {
    const start = new Date(v.scheduled_at);
    const end   = new Date(start.getTime() + (v.duration_mins||60) * 60000);
    lo = Math.min(lo, start.getHours());
    hi = Math.max(hi, end.getHours() + (end.getMinutes() > 0 ? 1 : 0));
  }
  return Array.from({ length: hi - lo + 1 }, (_, i) => String(lo + i).padStart(2, "0"));
}

// Default scheduled_at for the toolbar "일정 추가" button. The empty-slot
// click flow can use the clicked time directly; this is the fallback when
// the user clicks the button instead.
function nextHalfHour(d = new Date()) {
  const dt = new Date(d);
  dt.setSeconds(0, 0);
  const min = dt.getMinutes();
  dt.setMinutes(min < 30 ? 30 : 60);
  return dt;
}

// 툴바 "일정 추가" 버튼 프리필. 뷰가 오늘이 포함된 주면 지금+30분, 미래 주면 그 주
// 월요일 09:00. 과거 주는 서버 schedule_in_past? 백스톱이 있으므로 오늘로 폴백.
// (사용자가 ?date=로 미래 주를 열어놓고 버튼을 눌렀을 때 "왜 오늘로 뜨지" 혼란 방지.)
function nextHalfHourForWeek(weekStartStr) {
  const now = new Date();
  const ws  = parseYmd(weekStartStr);
  const we  = new Date(ws); we.setDate(ws.getDate() + 7);
  if (now >= ws && now < we) return nextHalfHour(now);
  if (ws > now) { const d = new Date(ws); d.setHours(9, 0, 0, 0); return d; }
  return nextHalfHour(now);
}

function startOfWeek(d = new Date()) {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1 - day);
  dt.setDate(dt.getDate() + diff);
  dt.setHours(0,0,0,0);
  return dt;
}

// 월간 뷰의 첫 화면 렌더 시각의 "달 1일"을 로컬 자정으로 반환. `?month=YYYY-MM`
// 쿼리를 우선 사용, 없으면 `?date=YYYY-MM-DD`가 속한 달, 그것도 없으면 오늘 달.
function ymPrefix(d) {
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}`;
}
function parseYm(s) {
  const [y, m] = String(s).split("-").map(Number);
  return new Date(y, m - 1, 1);
}

// 주 시작일을 서버와 주고받을 때는 로컬(KST) 달력 날짜로 다뤄야 한다.
// toISOString().slice(0,10)은 로컬 자정을 UTC로 옮겨 KST에서 하루 앞당겨진
// 날짜를 만들고(6/8 00:00 KST → "2026-06-07"), new Date("YYYY-MM-DD")는 반대로
// UTC 자정으로 파싱돼 카드가 한 칸 밀린다. 로컬 기준 포맷/파싱으로 통일한다.
function ymd(d) {
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
// datetime-local 입력의 min(=지금, 로컬). 과거 시각 선택을 막는다(백엔드 schedule_in_past? 백스톱).
// min은 step="900"(15분) 검증의 기준점이 되므로 15분 경계로 내림해야 한다. 그러지 않으면
// 유효값 그리드가 현재 분(예: :37)만큼 어긋나 둥근 시각(:00/:15…) 선택이 stepMismatch로 거부된다.
// 내림이라 min이 최대 14분 과거가 될 수 있으나 백엔드 schedule_in_past? 백스톱이 막는다.
function nowLocalInput() {
  const d = new Date();
  d.setMinutes(Math.floor(d.getMinutes() / 15) * 15, 0, 0);
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function parseYmd(s) {
  const [y, m, day] = String(s).split("-").map(Number);
  return new Date(y, m - 1, day); // 로컬 자정
}

// 대표 카드 선택 우선순위 — 위험도 높은 어르신이 항상 보이도록.
const RISK_RANK = { critical: 0, warning: 1, stable: 2 };

// 같은 날 시간대가 겹치는 방문들을 하나의 클러스터로 묶는다. 격자 열이 요일이라
// 동시간대 방문(서로 다른 매니저)은 같은 픽셀에 쌓이는데, 이를 대표 카드 1장 +
// "+n건" 배지로 접는다. 정렬 시작시각 기준으로 훑으며 running max-end 와 겹치면
// 같은 묶음(연결 성분). 각 묶음의 대표는 위험도순(동률이면 이른 시각).
function clusterDay(visits) {
  const sorted = [...visits].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const clusters = [];
  let cur = null, curEnd = -1;
  for (const v of sorted) {
    const startMs = new Date(v.scheduled_at).getTime();
    const endMs   = startMs + (v.duration_mins || 60) * 60000;
    if (cur && startMs < curEnd) {
      cur.items.push(v);
      curEnd = Math.max(curEnd, endMs);
    } else {
      cur = { items: [v] };
      curEnd = endMs;
      clusters.push(cur);
    }
  }
  for (const c of clusters) {
    const rep = [...c.items].sort((a, b) =>
      (RISK_RANK[a.elderly.risk_level] ?? 3) - (RISK_RANK[b.elderly.risk_level] ?? 3)
      || new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
    c.rep      = rep;
    c.topPx    = rep.topPx;
    c.heightPx = rep.heightPx;
  }
  return clusters;
}

function Schedule() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  // URL 쿼리로부터 초기 뷰 모드·기준일 결정. `?view=month` 면 월간, 아니면 주간.
  // 월간의 기준 달은 `?month=YYYY-MM` > `?date=YYYY-MM-DD` > 오늘 순으로 폴백.
  const initialParams = new URLSearchParams(window.location.search);
  const [viewMode, setViewMode] = React.useState(() =>
    initialParams.get("view") === "month" ? "month" : "week");
  const [weekStart, setWeekStart] = React.useState(() => {
    // Honour ?date=YYYY-MM-DD so the region dashboard can deep-link into
    // the week containing a specific day (e.g. today's visits drilldown).
    const raw = initialParams.get("date");
    const seed = raw ? parseYmd(raw) : new Date();
    if (isNaN(seed.getTime())) return ymd(startOfWeek());
    return ymd(startOfWeek(seed));
  });
  const [monthStart, setMonthStart] = React.useState(() => {
    const rawM = initialParams.get("month");
    if (rawM && /^\d{4}-\d{2}$/.test(rawM)) return rawM;
    const rawD = initialParams.get("date");
    const seed = rawD ? parseYmd(rawD) : new Date();
    return ymPrefix(isNaN(seed.getTime()) ? new Date() : seed);
  });
  const [q, setQ] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [riskFilter, setRiskFilter] = React.useState("all");
  const [managerFilter, setManagerFilter] = React.useState("all");
  // 서비스 유형(그룹) 필터 — 전체 / LTC / NLT / ER.
  const [serviceFilter, setServiceFilter] = React.useState("all");
  // 취소된 방문을 격자에서 숨길지(기본 ON). 데이터는 그대로 두고 보기에서만 제외하며,
  // 취소 KPI 집계는 영향받지 않는다(취소율 신호 유지). 상태 드롭다운에서 "취소"를
  // 명시 선택하면 이 토글과 무관하게 보여준다.
  const [hideCancelled, setHideCancelled] = React.useState(true);
  // null while no visit is open; the visit object when the detail modal is shown.
  const [openVisit, setOpenVisit] = React.useState(null);
  // { dateISO, scheduledAt } prefill when the user clicks an empty slot.
  const [creatingAt, setCreatingAt] = React.useState(null);
  // null, or an array of visits sharing a time slot (overflow "+n건" 클릭 시).
  const [openSlot, setOpenSlot] = React.useState(null);
  // 상세 모달을 슬롯 목록에서 열었을 때, 닫기를 누르면 돌아갈 그 목록을 기억한다.
  // (단일 카드에서 직접 연 경우는 null → 닫으면 캘린더로.)
  const [slotReturn, setSlotReturn] = React.useState(null);
  // Live drag-preview state. Mouse listeners are attached synchronously in
  // beginDrag (not via useEffect) so a fast click→release can't miss them.
  // Shape: { visit, mode:'move'|'resize', dayCol, topPx, heightPx, active }
  const [drag, setDrag] = React.useState(null);
  // Per-day column DOM nodes — used to hit-test which column the cursor is
  // over while dragging across days.
  const dayColRefs = React.useRef([]);
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });

  // 주간/월간 뷰가 동일 인덱스 엔드포인트를 공유한다(뷰 파라미터만 다름). 이름은
  // 관습상 loadWeek로 두되 두 모드 모두 커버.
  const loadWeek = React.useCallback(() => {
    const url = viewMode === "month"
      ? `/api/v1/visits?month=${monthStart}`
      : `/api/v1/visits?week_start=${weekStart}`;
    fetch(url, { credentials: "same-origin" })
      .then(r => r.json()).then(setData).catch(e => setErr(apiErrorMessage(e)));
  }, [viewMode, weekStart, monthStart]);
  React.useEffect(() => { loadWeek(); }, [loadWeek]);

  // URL 쿼리를 현재 상태와 동기화 — 새로고침/공유 링크가 같은 뷰로 열린다.
  // history.replaceState로 조용히 갱신(뒤로가기 스택 오염 방지).
  React.useEffect(() => {
    const u = new URL(window.location.href);
    if (viewMode === "month") {
      u.searchParams.set("view", "month");
      u.searchParams.set("month", monthStart);
      u.searchParams.delete("date");
    } else {
      u.searchParams.delete("view");
      u.searchParams.set("date", weekStart);
      u.searchParams.delete("month");
    }
    window.history.replaceState(null, "", u.toString());
  }, [viewMode, weekStart, monthStart]);

  if (err)   return <div className="app" style={{padding:40, color:'var(--rose-500)'}}>API 오류: {err}</div>;
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  // List of managers seen in this week — drives the manager filter
  // dropdown without needing a separate API call.
  const managersInWeek = (() => {
    const map = new Map();
    data.visits.forEach(v => map.set(v.care_manager.id, v.care_manager.name));
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  })();

  // Single pipeline: search → status → risk → manager.
  // KPI 집계는 `filteredVisits`(취소 포함) 기준 — 취소율 등 지표를 진실하게 유지.
  const needle = q.trim().toLowerCase();
  const filteredVisits = data.visits.filter(v => {
    if (needle && !(
      v.elderly.name.toLowerCase().includes(needle) ||
      v.care_manager.name.toLowerCase().includes(needle) ||
      (v.address_text||"").toLowerCase().includes(needle)
    )) return false;
    if (statusFilter !== "all" && v.status !== statusFilter) return false;
    if (riskFilter !== "all" && v.elderly.risk_level !== riskFilter) return false;
    if (managerFilter !== "all" && String(v.care_manager.id) !== String(managerFilter)) return false;
    if (serviceFilter !== "all") {
      // "none" = 서비스 코드 없는(레거시/자유) 방문. 나머지는 그룹 접두사(LTC/NLT/ER).
      if (serviceFilter === "none" && v.service_group) return false;
      if (serviceFilter !== "none" && v.service_group !== serviceFilter) return false;
    }
    return true;
  });

  // 격자(달력)에 실제로 그릴 목록 — 취소 숨김 토글 적용. 단, 상태 드롭다운에서
  // "취소"를 직접 고른 경우엔 명시 의도가 우선이라 숨기지 않는다.
  const gridVisits = (hideCancelled && statusFilter !== "cancelled")
    ? filteredVisits.filter(v => v.status !== "cancelled")
    : filteredVisits;

  // Apply a status change via PATCH and refetch the week. Keeping reload
  // server-driven means cancelled/in_progress timestamps stay authoritative.
  const changeStatus = async (visit, status) => {
    const r = await fetch(`/api/v1/visits/${visit.id}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visit: { status } }),
    });
    // 상태가 바뀌면 주간을 다시 불러오므로 슬롯 목록은 stale — 복귀 대신 캘린더로.
    if (r.ok) { setOpenVisit(null); setSlotReturn(null); loadWeek(); return; }
    const j = await r.json().catch(()=>({}));
    alert(j.message || "상태 변경에 실패했습니다.");
  };

  // PATCH a visit's scheduled_at and/or duration_mins. Used by drag-move and
  // resize. Optimistically updates `data` so the card stays at the dropped
  // position; refetch confirms (success) or reverts (failure / 409 conflict).
  const reschedule = async (visit, { scheduledAt, durationMins }) => {
    setData(d => d ? ({
      ...d,
      visits: d.visits.map(v => v.id === visit.id ? {
        ...v,
        scheduled_at:  scheduledAt  ?? v.scheduled_at,
        duration_mins: durationMins ?? v.duration_mins,
      } : v),
    }) : d);

    const body = { visit: {} };
    if (scheduledAt)  body.visit.scheduled_at  = scheduledAt;
    if (durationMins) body.visit.duration_mins = durationMins;
    const r = await fetch(`/api/v1/visits/${visit.id}`, {
      method: "PATCH", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (r.ok) { loadWeek(); return; }
    const j = await r.json().catch(() => ({}));
    if (r.status === 409 && j.conflict) {
      const who = j.kind === "elderly" ? "같은 어르신" : "같은 매니저";
      alert(`${who}의 충돌 일정이 있습니다 — ${j.conflict.elderly_name} · ${j.conflict.care_manager_name||''} · ${new Date(j.conflict.scheduled_at).toLocaleString('ko-KR')}`);
    } else {
      alert(j.message || "일정 변경에 실패했습니다.");
    }
    loadWeek();
  };

  // Snap a click position inside a day column to a SNAP_MINUTES-aligned ISO
  // timestamp. Operators can drop a planned visit at the slot they clicked.
  const handleEmptySlotClick = (e, dayDate) => {
    if (!perms.canWriteVisits) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const totalMinutes = Math.round((offsetY / PX_PER_HOUR) * 60);
    const snapped = Math.floor(totalMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    const dt = new Date(dayDate);
    dt.setHours(hourBase, snapped, 0, 0);
    setCreatingAt({ scheduledAt: dt.toISOString() });
  };

  const start = parseYmd(data.week_start);
  const today = new Date(); today.setHours(0,0,0,0);
  const DAYS = DAY_LABELS.map((d, i) => {
    const dt = new Date(start); dt.setDate(start.getDate() + i);
    return { d, dn: dt.getDate(), date: dt, today: dt.getTime() === today.getTime() };
  });

  const HOURS = computeHours(gridVisits);
  const hourBase = parseInt(HOURS[0], 10);

  // Start a drag (move or resize) on a visit card. Listeners are attached
  // here synchronously rather than via useEffect so a quick click→release
  // can't slip between render commits and miss the mouseup. A movement
  // threshold distinguishes a real drag from a plain click: below the
  // threshold the modal opens (same behaviour as before drag was added);
  // above it, we commit a PATCH on release.
  const beginDrag = (e, visit, mode, topPx, heightPx, dayCol) => {
    if (!perms.canWriteVisits) return;
    // Rescheduling a visit that's already happening (in_progress) or done
    // (completed/cancelled) is confusing and rarely intended. The detail
    // modal still allows admin overrides through status transitions.
    if (visit.status !== 'planned') return;
    e.preventDefault();
    e.stopPropagation();

    const SNAP_PX  = (SNAP_MINUTES / 60) * PX_PER_HOUR;
    const MIN_H_PX = SNAP_PX;          // visits are at least one snap unit
    const THRESHOLD = 4;               // px of movement to count as drag
    const startX = e.clientX, startY = e.clientY;
    const origTop = topPx, origH = heightPx;
    const gridHeight = HOURS.length * PX_PER_HOUR;
    // Capture week origin + hourBase at drag start. data refetches are gated
    // by mouse release, so these stay valid for the lifetime of the drag.
    const weekStart = parseYmd(data.week_start);
    const hourBaseLocal = hourBase;

    let active = false;
    let cur = { dayCol, topPx, heightPx };
    setDrag({ visit, mode, dayCol, topPx, heightPx, active: false });

    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keydown', onKey);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      if (!active && Math.hypot(ev.clientX - startX, dy) < THRESHOLD) return;
      active = true;

      if (mode === 'move') {
        let newCol = cur.dayCol;
        for (let i = 0; i < dayColRefs.current.length; i++) {
          const el = dayColRefs.current[i];
          if (!el) continue;
          const r = el.getBoundingClientRect();
          if (ev.clientX >= r.left && ev.clientX < r.right) { newCol = i; break; }
        }
        const rawTop = origTop + dy;
        const maxTop = Math.max(0, gridHeight - cur.heightPx);
        const top = Math.max(0, Math.min(maxTop, Math.round(rawTop / SNAP_PX) * SNAP_PX));
        cur = { ...cur, dayCol: newCol, topPx: top };
      } else {
        const rawH = origH + dy;
        const maxH = Math.max(MIN_H_PX, gridHeight - cur.topPx);
        const h = Math.max(MIN_H_PX, Math.min(maxH, Math.round(rawH / SNAP_PX) * SNAP_PX));
        cur = { ...cur, heightPx: h };
      }
      setDrag({ visit, mode, ...cur, active: true });
    };

    const onUp = () => {
      cleanup();
      setDrag(null);
      if (!active) { setOpenVisit(visit); return; }
      if (mode === 'move') {
        const dt = new Date(weekStart);
        dt.setDate(weekStart.getDate() + cur.dayCol);
        const totalMins = (cur.topPx / PX_PER_HOUR) * 60 + hourBaseLocal * 60;
        dt.setHours(0, totalMins, 0, 0);
        reschedule(visit, { scheduledAt: dt.toISOString() });
      } else {
        reschedule(visit, { durationMins: Math.round((cur.heightPx / PX_PER_HOUR) * 60) });
      }
    };

    const onKey = (ev) => {
      if (ev.key === 'Escape') { cleanup(); setDrag(null); }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keydown', onKey);
    document.body.style.cursor = mode === 'resize' ? 'ns-resize' : 'grabbing';
    document.body.style.userSelect = 'none';
  };

  const visitsByDay = Array(7).fill(0).map(() => []);
  gridVisits.forEach(v => {
    const dt = new Date(v.scheduled_at);
    const diff = Math.floor((dt - start) / 86400000);
    if (diff >= 0 && diff < 7) {
      // Continuous minute-precision positioning. 30-min, 45-min and 90-min
      // visits all land on the exact pixel rather than snapping to an hour.
      const offsetMin = (dt.getHours() - hourBase) * 60 + dt.getMinutes();
      visitsByDay[diff].push({
        ...v,
        topPx:   (offsetMin / 60) * PX_PER_HOUR,
        heightPx:((v.duration_mins||60) / 60) * PX_PER_HOUR,
        c: COLOR_FOR_RISK[v.elderly.risk_level || 'stable'] || 'green',
      });
    }
  });

  // 동시간대 방문을 묶어 대표 카드 + "+n건"으로 접는다. (위 clusterDay 참조)
  const clustersByDay = visitsByDay.map(clusterDay);

  // Overlay the live drag preview. 드래그 가능한 카드는 항상 단일 방문 클러스터
  // (클러스터 카드는 드래그 비활성)라 그 한 묶음만 이동/리사이즈한다. 클러스터링은
  // 드래그 이전 위치 기준이라 드래그 중 다른 묶음에 흡수되지 않고 위에 떠서 보인다.
  if (drag && drag.active) {
    for (let col = 0; col < 7; col++) {
      const idx = clustersByDay[col].findIndex(c => c.items.length === 1 && c.rep.id === drag.visit.id);
      if (idx < 0) continue;
      const c = clustersByDay[col][idx];
      if (drag.mode === 'move') {
        clustersByDay[col].splice(idx, 1);
        clustersByDay[drag.dayCol].push({ ...c, topPx: drag.topPx });
      } else {
        clustersByDay[col][idx] = { ...c, heightPx: drag.heightPx };
      }
      break;
    }
  }

  const totalVisits = filteredVisits.length;
  const completed   = filteredVisits.filter(v => v.status === 'completed').length;
  const critical    = filteredVisits.filter(v => v.elderly.risk_level === 'critical').length;
  // Visit.care_manager_id is NOT NULL in schema — there is no "unassigned"
  // visit. Surface "취소" instead, which is the actionable count operators
  // care about (cancelled = needs reschedule or follow-up).
  const cancelled   = filteredVisits.filter(v => v.status === 'cancelled').length;

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        {/* role prop 의도적으로 생략 — Sidebar가 user.role(SIDEBAR_ROLE_FOR_USER 매핑)을
            자동 인식. 매니저 로그인 시 매니저 메뉴가, 권역 운영자엔 권역 메뉴가 자동 노출.
            데이터는 visits_controller#scope_for_user가 이미 역할별로 스코프하므로 정합. */}
        <Sidebar active="schedule"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title={user?.role === "manager" ? "내 일정" : "일정 관리"}
                  sub={`${user?.role === "manager" ? "본인 방문" : "권역 전체"} · ${viewMode==="month"?"월간":"주간"} ${totalVisits}건`}/>
          <div className="main">

            <div className="card" style={{padding:'14px 22px', display:'flex', alignItems:'center', gap:14, marginBottom:14}}>
              <div style={{display:'flex', gap:0, border:'1px solid var(--border)', borderRadius:8, overflow:'hidden'}}>
                {/* 이전/다음: 주간이면 ±7일, 월간이면 ±1개월. */}
                <button className="btn btn-ghost btn-sm" style={{border:'none', borderRight:'1px solid var(--border)', borderRadius:0}}
                  onClick={() => {
                    if (viewMode === "month") {
                      const d = parseYm(monthStart); d.setMonth(d.getMonth()-1); setMonthStart(ymPrefix(d));
                    } else {
                      const d = parseYmd(weekStart); d.setDate(d.getDate()-7); setWeekStart(ymd(d));
                    }
                  }}>‹</button>
                <button className="btn btn-ghost btn-sm" style={{border:'none', borderRadius:0, padding:'6px 14px'}}
                  onClick={() => {
                    if (viewMode === "month") setMonthStart(ymPrefix(new Date()));
                    else setWeekStart(ymd(startOfWeek()));
                  }}>오늘</button>
                <button className="btn btn-ghost btn-sm" style={{border:'none', borderLeft:'1px solid var(--border)', borderRadius:0}}
                  onClick={() => {
                    if (viewMode === "month") {
                      const d = parseYm(monthStart); d.setMonth(d.getMonth()+1); setMonthStart(ymPrefix(d));
                    } else {
                      const d = parseYmd(weekStart); d.setDate(d.getDate()+7); setWeekStart(ymd(d));
                    }
                  }}>›</button>
              </div>
              <div className="h2" style={{fontSize:18}}>
                {viewMode === "month"
                  ? `${parseYm(monthStart).getFullYear()}년 ${parseYm(monthStart).getMonth()+1}월`
                  : `${start.getFullYear()}년 ${start.getMonth()+1}월 · ${start.getDate()} – ${(new Date(start.getTime()+6*86400000)).getDate()}`}
              </div>
              {/* 주/월 토글 — 세그먼티드 컨트롤 스타일. 선택된 쪽은 primary background. */}
              <div style={{display:'flex', gap:0, border:'1px solid var(--border)', borderRadius:8, overflow:'hidden'}}>
                {["week","month"].map(m => (
                  <button key={m} className="btn btn-sm"
                    style={{border:'none', borderRadius:0, padding:'6px 14px',
                      background: viewMode===m ? 'var(--ink-1)' : 'transparent',
                      color: viewMode===m ? '#fff' : 'var(--ink-2)', fontWeight:700}}
                    onClick={()=>setViewMode(m)}>
                    {m === "week" ? "주" : "월"}
                  </button>
                ))}
              </div>
              <div style={{flex:1}}/>
              {viewMode === "week" && (
                <a className="btn btn-ghost btn-md"
                  href={`/api/v1/visits/export.csv?week_start=${weekStart}`} style={{textDecoration:'none'}}>
                  {Ic.arrow} CSV
                </a>
              )}
              {perms.canWriteVisits && (
                <button className="btn btn-primary btn-md"
                  onClick={()=>setCreatingAt({ scheduledAt: nextHalfHourForWeek(weekStart).toISOString() })}>
                  {Ic.plus} 일정 추가
                </button>
              )}
            </div>

            <div className="card" style={{padding:'10px 16px', marginBottom:14, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
              <div className="searchbar" style={{flex:'0 0 240px', position:'relative'}}>
                <span style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--ink-3)'}}>{Ic.search}</span>
                <input className="input" style={{paddingLeft:36}}
                  placeholder="어르신·매니저·주소 검색"
                  value={q} onChange={e=>setQ(e.target.value)}/>
              </div>
              <select className="select" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{width:110}}>
                <option value="all">전체 상태</option>
                {Object.entries(STATUS_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select className="select" value={riskFilter} onChange={e=>setRiskFilter(e.target.value)} style={{width:110}}>
                <option value="all">전체 위험도</option>
                {Object.entries(RISK_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select className="select" value={serviceFilter} onChange={e=>setServiceFilter(e.target.value)} style={{width:130}}
                title="서비스 유형(장기요양·비장기요양·응급·미분류) 필터">
                <option value="all">전체 서비스</option>
                {Object.entries(SERVICE_GROUP_LABEL).map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                <option value="none">미분류</option>
              </select>
              <select className="select" value={managerFilter} onChange={e=>setManagerFilter(e.target.value)} style={{width:140}}>
                <option value="all">전체 매니저</option>
                {managersInWeek.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              {/* 취소 숨기기 — 기본 ON. 데이터는 유지하고 격자에서만 제외. 상태에서
                  "취소"를 직접 고르면 의미가 없어 비활성화한다. */}
              <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--ink-2)',
                cursor: statusFilter==='cancelled' ? 'not-allowed' : 'pointer', opacity: statusFilter==='cancelled' ? 0.5 : 1}}
                title={statusFilter==='cancelled' ? "상태 필터가 '취소'일 때는 적용되지 않습니다." : "취소된 방문을 달력에서 숨깁니다(집계는 유지)."}>
                <input type="checkbox" checked={hideCancelled} disabled={statusFilter==='cancelled'}
                  onChange={e=>setHideCancelled(e.target.checked)}/>
                취소 숨기기
              </label>
              <span className="muted" style={{fontSize:11.5, marginLeft:6}}>
                {gridVisits.length}건 / {viewMode==="month"?"월간":"주간"} {data.visits.length}건
              </span>
            </div>

            {viewMode === "month" && (
              <MonthGrid
                monthStart={monthStart}
                visits={gridVisits}
                onPickDay={(dayIso)=>{ setWeekStart(ymd(startOfWeek(parseYmd(dayIso)))); setViewMode("week"); }}
                onPickVisit={(v)=>setOpenVisit(v)}
                onPickSlot={(list)=>setOpenSlot(list)}
              />
            )}
            {viewMode === "week" && (
            <div className="card" style={{overflow:'hidden'}}>
              <div style={{display:'grid', gridTemplateColumns:'56px repeat(7, 1fr)', borderBottom:'1px solid var(--border-soft)'}}>
                <div style={{padding:'12px 8px', borderRight:'1px solid var(--border-soft)'}}/>
                {DAYS.map((d,i)=>(
                  <div key={i} style={{padding:'12px 8px', textAlign:'center', borderRight: i<6?'1px solid var(--border-soft)':'none', background: d.today?'var(--orange-50)':'transparent'}}>
                    <div style={{fontSize:10, color:'var(--ink-3)', fontWeight:700, letterSpacing:'0.06em'}}>{d.d}</div>
                    <div style={{fontSize:18, fontWeight:800, color: d.today?'var(--orange-600)':'var(--ink-1)', marginTop:2}}>{d.dn}</div>
                  </div>
                ))}
              </div>
              <div style={{display:'grid', gridTemplateColumns:'56px repeat(7, 1fr)', height: HOURS.length * PX_PER_HOUR, position:'relative'}}>
                <div style={{borderRight:'1px solid var(--border-soft)'}}>
                  {HOURS.map((h,i)=>(
                    <div key={i} style={{height:PX_PER_HOUR, padding:'4px 6px', fontSize:10, color:'var(--ink-3)', fontWeight:600, textAlign:'right', borderTop: i>0?'1px solid var(--border-soft)':'none'}}>{h}:00</div>
                  ))}
                </div>
                {DAYS.map((d,col)=>(
                  <div key={col}
                    ref={(el)=>{ dayColRefs.current[col] = el; }}
                    onClick={(e)=>handleEmptySlotClick(e, d.date)}
                    title={perms.canWriteVisits ? "빈 칸 클릭하여 일정 추가" : undefined}
                    style={{position:'relative', borderRight: col<6?'1px solid var(--border-soft)':'none', background: d.today?'rgba(232,130,91,0.04)':'transparent', cursor: perms.canWriteVisits ? 'cell' : 'default'}}>
                    {HOURS.map((h,r)=>(
                      <React.Fragment key={r}>
                        <div style={{height:PX_PER_HOUR/2, borderTop: r>0?'1px solid var(--border-soft)':'none'}}/>
                        <div style={{height:PX_PER_HOUR/2, borderTop:'1px dashed var(--border-soft)', opacity:0.5}}/>
                      </React.Fragment>
                    ))}
                    {clustersByDay[col].map((cluster) => {
                      const v = cluster.rep;
                      const extra = cluster.items.length - 1;
                      const isCluster = extra > 0;
                      // 클러스터(겹침) 카드는 드래그 비활성 — 클릭 시 슬롯 목록으로.
                      const isDraggable = perms.canWriteVisits && v.status === 'planned' && !isCluster;
                      const isDragging  = drag && drag.active && drag.visit.id === v.id;
                      return (
                        <div key={v.id}
                          onMouseDown={isDraggable ? (e) => beginDrag(e, v, 'move', cluster.topPx, cluster.heightPx, col) : undefined}
                          onClick={(e) => {
                            // Always swallow so the parent column's "add" handler
                            // doesn't fire when the user clicks a card.
                            e.stopPropagation();
                            // 겹침 묶음 → 그 시간대 전체 목록. 단일 카드 중 드래그
                            // 가능한 건 beginDrag mouseup(무이동)으로 상세를 열고,
                            // 비활성 단일 카드는 여기서 상세를 연다.
                            if (isCluster) setOpenSlot(cluster.items);
                            else if (!isDraggable) setOpenVisit(v);
                          }}
                          title={isCluster ? `이 시간대 ${cluster.items.length}건 · 클릭하여 전체 보기`
                                 : (isDraggable ? "드래그로 일정 이동 · 아래 가장자리로 시간 조절 · 클릭하여 상세" : "클릭하여 상세 보기")}
                          style={{
                            position:'absolute', left:4, right:4,
                            top: cluster.topPx + 2, height: cluster.heightPx - 4,
                            background: `var(--${v.c}-50)`,
                            border: `1.5px solid var(--${v.c}-500)`,
                            borderLeft: `4px solid var(--${v.c}-500)`,
                            borderRadius:8, padding:'5px 8px', fontSize:10.5, overflow:'hidden',
                            cursor: isDraggable ? 'grab' : 'pointer',
                            opacity: v.status==='cancelled' ? 0.5 : (isDragging ? 0.9 : 1),
                            // 겹침 카드는 "쌓인 더미" 느낌의 그림자로 묶음임을 암시.
                            boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.18)' : (isCluster ? '3px 3px 0 -1px var(--bg-sub), 3px 3px 0 var(--border)' : 'none'),
                            zIndex: isDragging ? 20 : 1,
                            userSelect: 'none',
                          }}>
                          <div style={{display:'flex', alignItems:'center', gap:4}}>
                            {v.service_group && (
                              <span style={{
                                fontSize:8.5, fontWeight:800, padding:'1px 4px',
                                background:`var(--${SERVICE_GROUP_COLOR[v.service_group]}-500)`,
                                color:'#fff', borderRadius:3, letterSpacing:0.3, flexShrink:0}}>
                                {v.service_group}
                              </span>
                            )}
                            <div style={{fontWeight:700, color:`var(--${v.c}-${v.c==='amber'?'500':'700'})`, fontSize:11,
                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0}}>
                              {v.elderly.name} · {v.address_text?.slice(0,8)}
                            </div>
                          </div>
                          <div className="muted" style={{fontSize:9.5, marginTop:1}}>
                            {v.care_manager.name}{v.service_label ? ` · ${v.service_label}` : ''}
                          </div>
                          {isCluster && (
                            <div style={{position:'absolute', right:4, bottom:3, fontSize:9.5, fontWeight:800,
                              color:'#fff', background:'var(--ink-2)', borderRadius:6, padding:'1px 6px', lineHeight:1.5}}>
                              +{extra}건
                            </div>
                          )}
                          {isDraggable && (
                            <div onMouseDown={(e) => beginDrag(e, v, 'resize', cluster.topPx, cluster.heightPx, col)}
                              title="드래그하여 소요 시간 조절"
                              style={{position:'absolute', left:0, right:0, bottom:0, height:6, cursor:'ns-resize'}}/>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            )}

            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginTop:14}}>
              {[
                {l: viewMode==="month" ? '월간 방문 계획' : '주간 방문 계획', v:`${totalVisits}건`, c:'orange'},
                {l:'완료', v:`${completed}건`, c:'green'},
                {l:'취소', v:`${cancelled}건`, c:'amber'},
                {l:'위험 케이스 방문', v:`${critical}건`, c:'rose'},
              ].map((s,i)=>(
                <div key={i} className="card kpi">
                  <div className="lbl">{s.l}</div>
                  <div className="val" style={{color:`var(--${s.c}-${s.c==='amber'?'500':'600'})`}}>{s.v}</div>
                </div>
              ))}
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      {openVisit && (
        <VisitDetailModal
          visit={openVisit}
          user={user}
          canWrite={perms.canWriteVisits}
          onClose={()=>{
            // 슬롯 목록에서 들어왔으면 그 "이 시간대 방문 N건" 화면으로 복귀.
            if (slotReturn) { setOpenSlot(slotReturn); setSlotReturn(null); }
            setOpenVisit(null);
          }}
          onChangeStatus={(s)=>changeStatus(openVisit, s)}
          onReschedule={async ({ scheduledAt, durationMins }) => {
            const body = { visit: {} };
            if (scheduledAt)  body.visit.scheduled_at  = scheduledAt;
            if (durationMins) body.visit.duration_mins = durationMins;
            const r = await fetch(`/api/v1/visits/${openVisit.id}`, {
              method: "PATCH", credentials: "same-origin",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            });
            if (r.ok) {
              // 방문이 다른 시간으로 이동 → 이전 슬롯 목록은 stale. 캘린더로 닫고 재조회.
              setSlotReturn(null); setOpenVisit(null); loadWeek();
              return;
            }
            const j = await r.json().catch(()=>({}));
            if (r.status === 409 && j.conflict) {
              const who = j.kind === "elderly" ? "같은 어르신" : "같은 매니저";
              alert(`${who}의 충돌 일정이 있습니다 — ${j.conflict.elderly_name} · ${j.conflict.care_manager_name||''} · ${new Date(j.conflict.scheduled_at).toLocaleString('ko-KR')}`);
            } else {
              alert(j.message || "일정 변경에 실패했습니다.");
            }
          }}
        />
      )}

      {creatingAt && (
        <VisitCreateModal
          prefill={creatingAt}
          onClose={()=>setCreatingAt(null)}
          onCreated={()=>{ setCreatingAt(null); loadWeek(); }}
        />
      )}

      {openSlot && (
        <SlotListModal
          items={openSlot}
          onClose={()=>setOpenSlot(null)}
          onPick={(v)=>{ setSlotReturn(openSlot); setOpenSlot(null); setOpenVisit(v); }}
        />
      )}
    </div>
  );
}

// 월간 캘린더 격자(6주×7일). 각 셀은 날짜 + 그날 방문의 미니 카드(최대 MAX_CARDS건,
// 시각순)를 표시한다. 넘치면 "+N 더보기" 배지로 접고 배지 클릭 시 슬롯 목록 모달 오픈.
// 셀의 빈 공간 클릭 → 그 날이 속한 주의 주간 뷰. 미니 카드 클릭 → 방문 상세 모달.
// 방문 데이터는 부모의 `gridVisits`(취소 숨김 필터 적용 후)를 그대로 사용한다.
const MONTH_CELL_MAX_CARDS = 3;
function MonthGrid({ monthStart, visits, onPickDay, onPickVisit, onPickSlot }) {
  const first = parseYm(monthStart);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const dt = new Date(gridStart); dt.setDate(gridStart.getDate() + i);
    return dt;
  });
  // 로컬 YMD로 버킷(서버 UTC vs. 로컬 KST 경계 이슈 없음 — Date 객체 로컬 사용).
  // 같은 날 안에서는 시각순 정렬해서 미니 카드도 자연스러운 순서로 보이게 한다.
  const byDay = new Map();
  for (const v of visits) {
    const key = ymd(new Date(v.scheduled_at));
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(v);
  }
  for (const list of byDay.values()) {
    list.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }
  const today = new Date(); today.setHours(0,0,0,0);
  const monthIdx = first.getMonth();
  const fmtTime = d => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  return (
    <div className="card" style={{overflow:'hidden'}}>
      <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', borderBottom:'1px solid var(--border-soft)'}}>
        {DAY_LABELS.map((d, i) => (
          <div key={i} style={{padding:'10px 8px', textAlign:'center', fontSize:11,
              color: (i===5||i===6) ? 'var(--rose-500)' : 'var(--ink-3)',
              fontWeight:700, letterSpacing:'0.06em',
              borderRight: i<6?'1px solid var(--border-soft)':'none'}}>
            {d}
          </div>
        ))}
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gridAutoRows:'minmax(120px, 1fr)'}}>
        {cells.map((dt, i) => {
          const key = ymd(dt);
          const items = byDay.get(key) || [];
          const inMonth = dt.getMonth() === monthIdx;
          const isToday = dt.getTime() === today.getTime();
          const col = i % 7;
          const row = Math.floor(i / 7);
          const shown = items.slice(0, MONTH_CELL_MAX_CARDS);
          const extra = items.length - shown.length;
          return (
            <div key={i}
              onClick={() => onPickDay(key)}
              title={`${dt.getMonth()+1}월 ${dt.getDate()}일 · ${items.length}건 · 빈 곳 클릭하여 주간 뷰`}
              style={{
                position:'relative', padding:'6px 6px 8px', cursor:'pointer',
                borderRight: col<6?'1px solid var(--border-soft)':'none',
                borderTop:   row>0?'1px solid var(--border-soft)':'none',
                background: isToday ? 'var(--orange-50)' : (inMonth ? 'transparent' : 'var(--bg-sub)'),
                opacity: inMonth ? 1 : 0.55,
                display:'flex', flexDirection:'column', gap:3, minHeight:0,
              }}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:6, padding:'0 2px'}}>
                <span style={{
                  fontSize:13, fontWeight:800,
                  color: isToday ? 'var(--orange-600)' :
                         (col===5||col===6) ? 'var(--rose-500)' : 'var(--ink-1)',
                }}>{dt.getDate()}</span>
                {items.length > 0 && (
                  <span style={{fontSize:10, color:'var(--ink-3)', fontWeight:700}}>{items.length}건</span>
                )}
              </div>
              {shown.map(v => {
                const c = COLOR_FOR_RISK[v.elderly.risk_level || 'stable'] || 'green';
                const isCancelled = v.status === 'cancelled';
                const sgColor = v.service_group ? SERVICE_GROUP_COLOR[v.service_group] : null;
                return (
                  <div key={v.id}
                    onClick={(e)=>{ e.stopPropagation(); onPickVisit(v); }}
                    title={`${fmtTime(new Date(v.scheduled_at))} · ${v.elderly.name} · ${v.care_manager.name}${v.service_label ? ` · ${v.service_label}` : ''} · ${STATUS_LABEL[v.status]||v.status}`}
                    style={{
                      display:'flex', alignItems:'center', gap:4,
                      padding:'2px 5px 2px 6px', borderRadius:4,
                      background:`var(--${c}-50)`,
                      borderLeft:`3px solid var(--${c}-500)`,
                      fontSize:10.5, lineHeight:1.25, cursor:'pointer',
                      opacity: isCancelled ? 0.55 : 1,
                      textDecoration: isCancelled ? 'line-through' : 'none',
                      minWidth:0,
                    }}>
                    {sgColor && (
                      <span style={{
                        width:6, height:6, borderRadius:2, flexShrink:0,
                        background:`var(--${sgColor}-500)`}} title={SERVICE_GROUP_LABEL[v.service_group]}/>
                    )}
                    <span className="mono" style={{fontWeight:700, color:'var(--ink-2)', flexShrink:0}}>
                      {fmtTime(new Date(v.scheduled_at))}
                    </span>
                    <span style={{fontWeight:600, color:'var(--ink-1)',
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0}}>
                      {v.elderly.name}
                    </span>
                  </div>
                );
              })}
              {extra > 0 && (
                <div
                  onClick={(e)=>{ e.stopPropagation(); onPickSlot(items); }}
                  title={`이 날 방문 ${items.length}건 전체 보기`}
                  style={{fontSize:10, fontWeight:700, color:'var(--ink-2)',
                    padding:'1px 6px', cursor:'pointer', borderRadius:4,
                    background:'var(--bg-sub)', alignSelf:'flex-start'}}>
                  +{extra}건 더보기
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 같은 시간대(겹치는 창)에 묶인 방문 전체 목록. 캘린더는 대표 카드 1장 + "+n건"
// 배지만 보여주므로, 클릭 시 이 모달로 묶인 방문을 모두 펼친다. 각 행을 누르면
// 기존 VisitDetailModal 로 넘어간다(시각순 정렬).
function SlotListModal({ items, onClose, onPick }) {
  useEscapeKey(onClose);
  const sorted = [...items].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const day = new Date(sorted[0].scheduled_at);
  const fmt = d => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  const fmtDate = d => `${d.getMonth()+1}월 ${d.getDate()}일 (${DAY_LABELS[(d.getDay()+6)%7]})`;
  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:52}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:460, padding:0, maxHeight:'80vh', display:'flex', flexDirection:'column'}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">이 시간대 방문 {items.length}건</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>{fmtDate(day)} · {fmt(new Date(sorted[0].scheduled_at))} 부터</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>
        <div style={{overflow:'auto', padding:'6px 0'}}>
          {sorted.map(v => {
            const dt  = new Date(v.scheduled_at);
            const end = new Date(dt.getTime() + (v.duration_mins||60)*60000);
            const rc  = COLOR_FOR_RISK[v.elderly.risk_level] || 'gray';
            const sc  = STATUS_COLOR[v.status] || 'gray';
            return (
              <button key={v.id} onClick={()=>onPick(v)}
                style={{display:'flex', alignItems:'center', gap:12, width:'100%', textAlign:'left',
                  padding:'10px 22px', border:'none', background:'transparent', cursor:'pointer',
                  borderLeft:`4px solid var(--${rc}-500)`}}
                onMouseEnter={e=>e.currentTarget.style.background='var(--bg-sub)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div className="mono" style={{fontSize:12, fontWeight:700, width:104, flexShrink:0}}>{fmt(dt)}–{fmt(end)}</div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:700, fontSize:12.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {v.elderly.name} <span className="muted" style={{fontWeight:400}}>· {v.elderly.age}세</span>
                  </div>
                  <div className="muted" style={{fontSize:11, marginTop:1}}>{v.care_manager.name} 매니저</div>
                </div>
                <span className={"badge badge-"+sc} style={{flexShrink:0}}>{STATUS_LABEL[v.status]||v.status}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Visit detail + status transition modal. The available actions depend on
// the current status so operators can only make valid transitions
// (planned→진행/취소, in_progress→완료/취소, completed→되돌리기 only via
// API-side audit override — UI keeps it forward-only).
function VisitDetailModal({ visit, user, onClose, onChangeStatus, onReschedule, canWrite = true }) {
  useEscapeKey(onClose);
  const dt = new Date(visit.scheduled_at);
  const end = new Date(dt.getTime() + (visit.duration_mins||60) * 60000);
  const fmt = d => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  const fmtDate = d => `${d.getMonth()+1}월 ${d.getDate()}일 (${DAY_LABELS[(d.getDay()+6)%7]})`;
  const next = STATUS_NEXT[visit.status];
  const canCancel = visit.status !== "cancelled" && visit.status !== "completed";
  const isSysAdmin = user?.role === "system_admin";
  // System admins can roll back terminal states (completed/cancelled) for
  // correcting accidental check-outs. Audit always records the transition.
  const revertTo = isSysAdmin && (visit.status === "completed" ? "in_progress" :
                                   visit.status === "cancelled" ? "planned"     : null);
  const sb = { color: STATUS_COLOR[visit.status] || "gray", label: STATUS_LABEL[visit.status] || visit.status };
  const rb = { color: COLOR_FOR_RISK[visit.elderly.risk_level] || "gray", label: RISK_LABEL[visit.elderly.risk_level] || "-" };

  // 일시 변경(재배정) — 묶음에 가려져 드래그할 수 없는 카드도 여기서 시간을 옮긴다.
  // 드래그와 동일하게 planned 상태에서만 허용한다. onReschedule 은 충돌 시 alert 후
  // 모달 유지, 성공 시 부모가 모달을 닫는다(주간 재조회).
  const canReschedule = canWrite && visit.status === "planned" && typeof onReschedule === "function";
  const [editing, setEditing] = React.useState(false);
  const [schedAt, setSchedAt] = React.useState(visit.scheduled_at);
  const [dur, setDur]         = React.useState(visit.duration_mins || 60);
  const [saving, setSaving]   = React.useState(false);
  const editLocalValue = (() => {
    const d = new Date(schedAt);
    const p = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  })();
  const saveReschedule = async () => {
    setSaving(true);
    try { await onReschedule({ scheduledAt: schedAt, durationMins: dur }); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:520, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">방문 #{visit.id}</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>{fmtDate(dt)} · {fmt(dt)} → {fmt(end)} ({visit.duration_mins||60}분)</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        <div style={{padding:'18px 22px', display:'grid', gridTemplateColumns:'120px 1fr', gap:'10px 14px', fontSize:12.5}}>
          <div className="muted">어르신</div>
          <div>
            <b>{visit.elderly.name}</b> · {visit.elderly.age}세
            <span className={"badge badge-" + rb.color} style={{marginLeft:8}}>{rb.label}</span>
          </div>
          <div className="muted">담당 매니저</div>
          <div><b>{visit.care_manager.name}</b> <span className="muted">· {visit.care_manager.qualification}</span></div>
          <div className="muted">방문 주소</div>
          <div>{visit.address_text || visit.elderly.address || "-"}</div>
          <div className="muted">현재 상태</div>
          <div><span className={"badge badge-" + sb.color}>{sb.label}</span></div>
          {visit.service_code && (<>
            <div className="muted">서비스 유형</div>
            <div>
              {visit.service_group && (
                <span className={"badge badge-" + (SERVICE_GROUP_COLOR[visit.service_group] || 'gray')} style={{marginRight:6}}>
                  {SERVICE_GROUP_LABEL[visit.service_group] || visit.service_group}
                </span>
              )}
              <span className="mono" style={{fontSize:11}}>{visit.service_code}</span>
              {visit.service_label && <span style={{marginLeft:6}}>· {visit.service_label}</span>}
              {visit.service_plan_item_id && (
                <span className="muted" style={{fontSize:10.5, marginLeft:6}}>· 계획 항목 연결</span>
              )}
            </div>
          </>)}
          {visit.check_in_at && (<><div className="muted">체크인</div><div className="mono">{new Date(visit.check_in_at).toLocaleString('ko-KR')}</div></>)}
          {visit.check_out_at && (<><div className="muted">체크아웃</div><div className="mono">{new Date(visit.check_out_at).toLocaleString('ko-KR')}</div></>)}
        </div>

        {canReschedule && editing && (
          <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', background:'var(--bg-sub)'}}>
            <div className="label" style={{marginBottom:8}}>일시 변경</div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 140px', gap:12}}>
              <div>
                <div className="label" style={{fontSize:11}}>방문 일시</div>
                <input className="input" type="datetime-local" value={editLocalValue} step="900" min={nowLocalInput()}
                  onChange={e=>setSchedAt(new Date(e.target.value).toISOString())}/>
              </div>
              <div>
                <div className="label" style={{fontSize:11}}>소요 시간</div>
                <select className="select" value={dur} onChange={e=>setDur(+e.target.value)}>
                  {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d}분</option>)}
                </select>
              </div>
            </div>
            <div style={{display:'flex', justifyContent:'flex-end', gap:8, marginTop:12}}>
              <button className="btn btn-ghost btn-sm"
                onClick={()=>{ setEditing(false); setSchedAt(visit.scheduled_at); setDur(visit.duration_mins||60); }}>취소</button>
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={saveReschedule}>
                {saving ? '저장 중…' : '변경 저장'}
              </button>
            </div>
          </div>
        )}

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'space-between', gap:8, alignItems:'center'}}>
          <div style={{display:'flex', gap:8}}>
            {canReschedule && !editing && (
              <button className="btn btn-ghost btn-md" onClick={()=>setEditing(true)}>일시 변경</button>
            )}
            {canWrite && canCancel && (
              <button className="btn btn-ghost btn-md" onClick={()=>onChangeStatus("cancelled")} style={{color:'var(--rose-500)'}}>방문 취소</button>
            )}
            {canWrite && revertTo && (
              <button className="btn btn-ghost btn-md" onClick={()=>onChangeStatus(revertTo)}
                title="시스템 관리자 권한으로 상태를 되돌립니다 (감사 기록 자동)">
                ↶ {STATUS_LABEL[revertTo]}으로 되돌리기
              </button>
            )}
          </div>
          <div style={{display:'flex', gap:8}}>
            <button className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
            {canWrite && next && (
              <button className="btn btn-primary btn-md" onClick={()=>onChangeStatus(next)}>
                {STATUS_LABEL[next]}으로 변경 →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// New visit modal. Loaded the region-scoped elderly + manager picklists
// from /api/v1/visits/candidates on mount. Server returns 409 on conflict
// (same manager overlap), which we surface inline so operators can pick a
// different time or manager without losing what they typed.
const DURATION_OPTIONS = [30, 45, 60, 75, 90, 120];

function VisitCreateModal({ prefill, onClose, onCreated }) {
  useEscapeKey(onClose);
  const [elderlies, setElderlies]     = React.useState([]);
  const [managers, setManagers]       = React.useState([]);
  const [serviceTypes, setServiceTypes] = React.useState({}); // 카탈로그: { ltc:[…], non_ltc:[…], emergency:[…] }
  const [elderlyId, setElderlyId]     = React.useState("");
  const [managerId, setManagerId]     = React.useState("");
  // 서비스 코드 / 계획 항목 — 어르신에 활성 계획 항목이 있으면 자동 선택.
  const [serviceCode, setServiceCode]     = React.useState("");
  const [planItemId, setPlanItemId]       = React.useState("");
  // actorCareManagerId 가 set 되면 매니저 본인이 자기 일정을 추가하는 흐름.
  // 매니저 필드는 본인으로 잠금 표시하고, 어르신 picklist 는 본인 담당으로 좁혀진다.
  const [actorCareManagerId, setActorCareManagerId] = React.useState(null);
  const [scheduledAt, setScheduledAt] = React.useState(prefill.scheduledAt);
  const [duration, setDuration]       = React.useState(60);
  const [err, setErr]                 = React.useState(null);
  const [conflict, setConflict]       = React.useState(null);
  const [submitting, setSubmitting]   = React.useState(false);

  React.useEffect(() => {
    fetch("/api/v1/visits/candidates", { credentials: "same-origin" })
      .then(r => r.json())
      .then(j => {
        setElderlies(j.elderlies || []);
        setManagers(j.care_managers || []);
        setServiceTypes(j.service_types || {});
        if (j.actor_care_manager_id) {
          setActorCareManagerId(j.actor_care_manager_id);
          setManagerId(String(j.actor_care_manager_id));
        }
        if (!j.can_write) setErr("일정 추가 권한이 없습니다.");
      })
      .catch(e => setErr(apiErrorMessage(e)));
  }, []);

  const isSelfFlow = actorCareManagerId != null;
  const actorManager = isSelfFlow ? managers.find(m => String(m.id) === String(actorCareManagerId)) : null;

  // 선택한 어르신의 실제 담당 매니저(열린 케이스 기준). 운영자가 어르신을 고르면
  // 매니저 기본값을 이 사람으로 채우고, 다른 매니저로 바꾸면 대체 배정임을 안내한다.
  const selectedElderly = elderlies.find(e => String(e.id) === String(elderlyId));
  const assignedManager = selectedElderly && selectedElderly.care_manager_id
    ? managers.find(m => String(m.id) === String(selectedElderly.care_manager_id)) : null;
  const isSubstitute = !isSelfFlow && assignedManager && String(managerId) !== String(assignedManager.id);

  // 어르신 선택 → 담당 매니저 + 활성 계획의 첫 항목(우선순위순)을 기본 선택.
  const pickElderly = id => {
    setElderlyId(id);
    const eld = elderlies.find(e => String(e.id) === String(id));
    if (eld && !isSelfFlow && eld.care_manager_id) setManagerId(String(eld.care_manager_id));
    // 계획 항목 자동 선택 — 서비스 코드도 같이 채움. 우선순위(priority) 낮은 값이 첫순위.
    const items = eld?.plan_items || [];
    const top   = items[0];
    if (top) {
      setPlanItemId(String(top.id));
      setServiceCode(top.service_code);
      if (top.duration_mins) setDuration(top.duration_mins);
    } else {
      setPlanItemId("");
      setServiceCode("");
    }
  };

  // 계획 항목이 있는 어르신인지. UI에서 선택박스 노출 분기.
  const currentPlanItems = selectedElderly?.plan_items || [];

  const pickPlanItem = id => {
    setPlanItemId(id);
    if (!id) { setServiceCode(""); return; }
    const it = currentPlanItems.find(x => String(x.id) === String(id));
    if (it) {
      setServiceCode(it.service_code);
      if (it.duration_mins) setDuration(it.duration_mins);
    }
  };

  // Convert the ISO timestamp into the value=YYYY-MM-DDTHH:MM format the
  // datetime-local input expects (no seconds, no zone).
  const localValue = (() => {
    const d = new Date(scheduledAt);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  })();

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null); setConflict(null);
    if (!elderlyId) return setErr("어르신을 선택해주세요.");
    if (!managerId) return setErr("매니저를 선택해주세요.");
    setSubmitting(true);
    try {
      const visitBody = {
        care_manager_id: +managerId, elderly_id: +elderlyId,
        scheduled_at: scheduledAt, duration_mins: duration,
      };
      if (planItemId)  visitBody.service_plan_item_id = +planItemId;
      if (serviceCode) visitBody.service_code         = serviceCode;
      const r = await fetch("/api/v1/visits", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visit: visitBody }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { setConflict({ ...j.conflict, kind: j.kind }); return; }
      if (!r.ok) { setErr(apiErrorMessage(j, r.status)); return; }
      onCreated();
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setSubmitting(false); }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:55}}>
      {/* noValidate: <select required> 의 네이티브 검증이 조용히 submit을 막으면
          JS의 setErr("매니저를 선택해주세요.") 배너가 뜨지 못한다(핸들러가 아예 미호출).
          어르신을 골랐지만 담당 매니저가 없는(열린 케이스 없음) 경우가 흔해 이 경로가
          자주 밟힌다. 검증은 submit 핸들러가 책임지고 사용자에게 사유를 보여준다. */}
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} noValidate className="card" style={{width:520, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">{isSelfFlow ? "내 일정 추가" : "일정 추가"}</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              {isSelfFlow && actorManager && <>담당: <b>{actorManager.name}</b> · </>}
              {new Date(scheduledAt).toLocaleString('ko-KR')}
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}
        {conflict && (
          <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--amber-50)', color:'#8a6326', borderRadius:10, fontSize:12.5}}>
            ⚠ 같은 {conflict.kind === "elderly" ? "어르신" : "매니저"}의 충돌 일정이 있습니다 — <b>{conflict.elderly_name}</b>
            {conflict.care_manager_name && <> · {conflict.care_manager_name} 매니저</>} · {new Date(conflict.scheduled_at).toLocaleString('ko-KR')} · {conflict.duration_mins}분
            <div style={{fontSize:11.5, marginTop:4}}>
              {conflict.kind === "elderly" ? "시간이나 어르신을 변경해주세요." : "시간이나 매니저를 변경해주세요."}
            </div>
          </div>
        )}

        <div style={{padding:22, display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
          <div style={{gridColumn:'1 / span 2'}}>
            <div className="label">
              어르신 *
              {isSelfFlow && <span className="muted" style={{fontSize:11, fontWeight:400, marginLeft:6}}>(내 담당 어르신만 표시)</span>}
            </div>
            <select className="select" value={elderlyId} onChange={e=>pickElderly(e.target.value)} required>
              <option value="">선택…</option>
              {elderlies.map(e => <option key={e.id} value={e.id}>{e.name} · {e.age}세 · {RISK_LABEL[e.risk_level]||"-"}</option>)}
            </select>
            {isSelfFlow && elderlies.length === 0 && (
              <div className="muted" style={{fontSize:11, marginTop:6}}>담당 중인 어르신이 없습니다. 권역 운영자에게 케이스 배정을 요청해주세요.</div>
            )}
          </div>
          <div style={{gridColumn:'1 / span 2'}}>
            <div className="label">담당 매니저 *</div>
            {isSelfFlow && actorManager ? (
              <div className="select" style={{display:'flex', alignItems:'center', background:'var(--bg-sub)', color:'var(--ink-2)', cursor:'not-allowed'}}
                   title="본인 일정만 추가할 수 있습니다. 다른 매니저 배정은 권역 운영자에게 요청해주세요.">
                <b style={{color:'var(--ink-1)'}}>{actorManager.name}</b>
                <span style={{marginLeft:6}}>· {actorManager.qualification}</span>
              </div>
            ) : (
              <select className="select" value={managerId} onChange={e=>setManagerId(e.target.value)} required>
                <option value="">선택…</option>
                {managers.map(m => <option key={m.id} value={m.id}>{m.name} · {m.qualification}</option>)}
              </select>
            )}
            {isSubstitute && (
              <div className="muted" style={{fontSize:11, marginTop:6, color:'#8a6326'}}>
                담당 매니저(<b>{assignedManager.name}</b>)가 아닙니다 — 대체 방문으로 등록됩니다.
              </div>
            )}
            {!isSelfFlow && selectedElderly && !assignedManager && (
              <div className="muted" style={{fontSize:11, marginTop:6}}>
                이 어르신의 담당 매니저가 지정돼 있지 않습니다(열린 케이스 없음).
              </div>
            )}
          </div>
          <div>
            <div className="label">방문 일시 *</div>
            <input className="input" type="datetime-local" value={localValue} step="900" min={nowLocalInput()}
              onChange={e=>setScheduledAt(new Date(e.target.value).toISOString())} required/>
          </div>
          <div>
            <div className="label">소요 시간</div>
            <select className="select" value={duration} onChange={e=>setDuration(+e.target.value)}>
              {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d}분</option>)}
            </select>
          </div>

          {/* 서비스 코드 — 어르신 활성 계획에 항목이 있으면 계획 항목 선택 UI, 아니면
              전체 카탈로그(LTC/NLT/ER) 에서 free-form 선택. 계획 항목 선택 시 코드는
              자동 파생되고 duration 도 항목 기본값으로 채워진다(사용자 수정 허용). */}
          {selectedElderly && currentPlanItems.length > 0 ? (
            <div style={{gridColumn:'1 / span 2'}}>
              <div className="label">계획 항목 <span className="muted" style={{fontSize:11, fontWeight:400}}>(활성 서비스계획에서 선택)</span></div>
              <select className="select" value={planItemId} onChange={e=>pickPlanItem(e.target.value)}>
                <option value="">(계획 외 · 자유 등록)</option>
                {currentPlanItems.map(it => (
                  <option key={it.id} value={it.id}>
                    P{it.priority} · {it.service_code} · {it.label}
                    {it.frequency_per_week ? ` · 주 ${it.frequency_per_week}회` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {(!selectedElderly || currentPlanItems.length === 0 || !planItemId) && (
            <div style={{gridColumn:'1 / span 2'}}>
              <div className="label">서비스 유형 <span className="muted" style={{fontSize:11, fontWeight:400}}>(선택 안 함도 가능)</span></div>
              <select className="select" value={serviceCode} onChange={e=>setServiceCode(e.target.value)}>
                <option value="">— 미분류 —</option>
                {["ltc","non_ltc","emergency"].flatMap(group => {
                  const items = (serviceTypes[group] || []);
                  if (items.length === 0) return [];
                  const label = group === "ltc" ? "장기요양" : group === "non_ltc" ? "비장기요양" : "응급·병원";
                  return [
                    <optgroup key={group} label={label}>
                      {items.map(it => (
                        <option key={it.code} value={it.code}>{it.code} · {it.label}</option>
                      ))}
                    </optgroup>
                  ];
                })}
              </select>
            </div>
          )}
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
          <button type="submit" className="btn btn-primary btn-md" disabled={submitting}>
            {submitting ? '추가 중…' : '일정 추가'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { Schedule };
